import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, ChevronUp, ChevronDown, ClipboardList, LayoutDashboard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const money = (n) => Number(n || 0);

function Builder() {
  const [tmpl, setTmpl] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSection, setNewSection] = useState("");
  const [newTask, setNewTask] = useState({});
  const load = useCallback(() => { setLoading(true); api.get("/admin/worksheet/template").then((r) => setTmpl(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const addSection = async () => { if (!newSection.trim()) return; await api.post("/admin/worksheet/sections", { title: newSection }); setNewSection(""); load(); };
  const editSection = async (s) => { const t = window.prompt("Rename section", s.title); if (t?.trim()) { await api.patch(`/admin/worksheet/sections/${s.id}`, { title: t.trim() }); load(); } };
  const delSection = async (s) => { if (window.confirm(`Delete section "${s.title}" and its tasks?`)) { await api.delete(`/admin/worksheet/sections/${s.id}`); load(); } };
  const addTask = async (sid) => { const v = (newTask[sid] || "").trim(); if (!v) return; await api.post("/admin/worksheet/tasks", { section_id: sid, task_name: v }); setNewTask({ ...newTask, [sid]: "" }); load(); };
  const editTask = async (t) => { const v = window.prompt("Edit task", t.task_name); if (v?.trim()) { await api.patch(`/admin/worksheet/tasks/${t.id}`, { task_name: v.trim() }); load(); } };
  const delTask = async (t) => { await api.delete(`/admin/worksheet/tasks/${t.id}`); load(); };
  const move = async (arr, idx, dir, kind, sid) => {
    const j = idx + dir; if (j < 0 || j >= arr.length) return;
    const order = arr.map((x) => x.id); [order[idx], order[j]] = [order[j], order[idx]];
    await api.post(`/admin/worksheet/${kind}/reorder`, { order, section_id: sid || "" }); load();
  };

  if (loading) return <Loader2 className="animate-spin text-rose mx-auto my-8" />;
  return (
    <div data-testid="worksheet-builder">
      <div className="card-lux p-4 mb-4 flex items-end gap-2">
        <div className="flex-1"><label className="text-xs text-stone-500">New section</label>
          <input value={newSection} onChange={(e) => setNewSection(e.target.value)} data-testid="ws-new-section" className="w-full mt-1 rounded-lg border border-stone-200 px-3 py-2 text-sm" placeholder="e.g. Laundry Room" /></div>
        <button onClick={addSection} data-testid="ws-add-section" className="btn-primary text-sm"><Plus size={15} /> Add section</button>
      </div>
      <div className="space-y-4">
        {tmpl.map((s, si) => (
          <div key={s.id} className="card-lux p-4" data-testid={`ws-section-${s.id}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-serif text-lg text-forest">{s.title} <span className="text-xs text-stone-400">({s.tasks.length})</span></h4>
              <div className="flex gap-1">
                <button onClick={() => move(tmpl, si, -1, "sections")} className="p-1.5 rounded bg-stone-100"><ChevronUp size={14} /></button>
                <button onClick={() => move(tmpl, si, 1, "sections")} className="p-1.5 rounded bg-stone-100"><ChevronDown size={14} /></button>
                <button onClick={() => editSection(s)} className="p-1.5 rounded bg-stone-100"><Pencil size={14} /></button>
                <button onClick={() => delSection(s)} className="p-1.5 rounded bg-red-50 text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="space-y-1.5">
              {s.tasks.map((t, ti) => (
                <div key={t.id} className="flex items-center gap-2 text-sm bg-sand/40 rounded px-2.5 py-1.5">
                  <span className="flex-1">{t.task_name}</span>
                  <button onClick={() => move(s.tasks, ti, -1, "tasks", s.id)} className="p-1 rounded hover:bg-white"><ChevronUp size={13} /></button>
                  <button onClick={() => move(s.tasks, ti, 1, "tasks", s.id)} className="p-1 rounded hover:bg-white"><ChevronDown size={13} /></button>
                  <button onClick={() => editTask(t)} className="p-1 rounded hover:bg-white"><Pencil size={13} /></button>
                  <button onClick={() => delTask(t)} className="p-1 rounded text-red-600 hover:bg-white"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <input value={newTask[s.id] || ""} onChange={(e) => setNewTask({ ...newTask, [s.id]: e.target.value })} data-testid={`ws-new-task-${s.id}`} className="flex-1 rounded-lg border border-stone-200 px-3 py-1.5 text-sm" placeholder="Add task…" onKeyDown={(e) => e.key === "Enter" && addTask(s.id)} />
              <button onClick={() => addTask(s.id)} data-testid={`ws-add-task-${s.id}`} className="text-xs px-3 rounded-lg bg-forest text-cream font-semibold"><Plus size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const load = useCallback(() => api.get(`/admin/worksheet/worksheets${status ? `?status=${status}` : ""}`).then((r) => setData(r.data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);
  const openPdf = async (w) => { try { const r = await api.get(`/worksheet/worksheets/${w.id}/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 60000); } catch { toast.error("PDF unavailable"); } };
  if (!data) return <Loader2 className="animate-spin text-rose mx-auto my-8" />;
  const cards = [["Total worksheets", data.stats.total], ["Completed today", data.stats.completed_today], ["Outstanding", data.stats.outstanding], ["Avg completion", `${data.stats.avg_completion}%`]];
  return (
    <div data-testid="worksheet-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {cards.map(([l, v]) => <div key={l} className="card-lux p-4"><div className="text-xs text-stone-500">{l}</div><div className="font-serif text-2xl text-forest mt-1">{v}</div></div>)}
      </div>
      <div className="mb-3"><select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ws-status-filter" className="rounded-lg border border-stone-200 px-3 py-2 text-sm"><option value="">All statuses</option><option value="in_progress">In progress</option><option value="completed">Completed</option><option value="reviewed">Reviewed</option></select></div>
      <div className="card-lux overflow-hidden"><div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="ws-table"><thead className="bg-sand/60 text-stone-500 text-xs uppercase"><tr><th className="text-left p-3">Date</th><th className="text-left p-3">Cleaner</th><th className="text-left p-3">Property</th><th className="text-left p-3">Status</th><th className="text-left p-3">Completion</th><th className="text-left p-3">PDF</th></tr></thead>
          <tbody>
            {data.worksheets.map((w) => (
              <tr key={w.id} className="border-t border-stone-100">
                <td className="p-3 whitespace-nowrap">{w.date}</td><td className="p-3">{w.staff_name}</td><td className="p-3">{w.property_name}</td>
                <td className="p-3 capitalize">{(w.status || "").replace("_", " ")}</td>
                <td className="p-3 font-semibold">{money(w.completion_percent)}%</td>
                <td className="p-3"><button onClick={() => openPdf(w)} className="text-xs text-rose font-semibold underline">Open</button></td>
              </tr>
            ))}
            {data.worksheets.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-stone-400">No worksheets yet.</td></tr>}
          </tbody></table>
      </div></div>
    </div>
  );
}

export default function WorksheetAdmin() {
  const [sub, setSub] = useState("dashboard");
  return (
    <div data-testid="worksheet-admin">
      <div className="flex gap-2 mb-5 border-b border-stone-200 pb-3">
        <button onClick={() => setSub("dashboard")} data-testid="ws-subtab-dashboard" className={`text-sm px-4 py-2 rounded-full font-medium inline-flex items-center gap-2 ${sub === "dashboard" ? "bg-rose text-white" : "bg-sand text-forest"}`}><LayoutDashboard size={15} /> Completion Dashboard</button>
        <button onClick={() => setSub("builder")} data-testid="ws-subtab-builder" className={`text-sm px-4 py-2 rounded-full font-medium inline-flex items-center gap-2 ${sub === "builder" ? "bg-rose text-white" : "bg-sand text-forest"}`}><ClipboardList size={15} /> Template Builder</button>
      </div>
      {sub === "dashboard" ? <Dashboard /> : <Builder />}
    </div>
  );
}
